/// <amd-module name="Feed/Legacy/Protocols/NuGet/Commands/NuGetUnlistCommand" />
import { announce } from "VSS/Core/Util/Accessibility";
import * as Utils_String from "VSS/Core/Util/String";
import { IVssPageContext } from "VSS/Platform/Context";

import { Package, PackageVersion } from "Feed/Common/Contracts/Feed";
import { PackageVersionDetails } from "Feed/Common/Protocols/NuGet/Contracts/NuGet";
import * as PackageResources from "Feed/Common/Resources";
import { IError } from "Feed/Common/Types/IError";
import { IGeneralDialogProps } from "Feed/Common/Types/IGeneralDialogProps";
import * as Actions from "Feed/Legacy/Actions/Actions";
import { IPackagePayload } from "Feed/Legacy/Common/ActionPayloads";
import { ProtocolCommands } from "Feed/Legacy/Common/ProtocolCommands";
import { CustomerIntelligenceHelper } from "Feed/Legacy/Helpers/CustomerIntelligenceHelper";
import { NuGetCiConstants } from "Feed/Legacy/Protocols/NuGet/Constants/NuGetConstants";
import { IFeedNuGetDataService } from "Feed/Legacy/Protocols/NuGet/NuGetDataService";
import { IPackageCommand } from "Feed/Legacy/Types/IPackageCommand";
import { Feed } from "Feed/Legacy/WebApi/VSS.CustomFeed.Contracts";

export class UnlistPackagesCommand implements IPackageCommand {
    public readonly id: string;
    public readonly protocolCommand: ProtocolCommands;
    public readonly displayText: string;
    public readonly titleText: string;
    public readonly icon: string;
    public readonly disabled: boolean;
    public readonly actionMethod: () => void;

    constructor(id: string, displayText: string, dialogProps: IGeneralDialogProps, disabled: boolean) {
        this.id = id;
        this.protocolCommand = ProtocolCommands.Unlist;
        this.displayText = displayText;
        this.icon = "bowtie-icon bowtie-math-minus";
        this.disabled = disabled;
        this.actionMethod = () => Actions.DialogOpenChanged.invoke(dialogProps);
    }
}

// This class is used by the old dialogs and can be removed when the feature flag for new dialogs is removed.
export class UnlistPackageCommand implements IPackageCommand {
    public readonly id: string;
    public readonly protocolCommand: ProtocolCommands;
    public readonly displayText: string;
    public readonly titleText: string;
    public readonly icon: string;
    public readonly disabled: boolean;
    public readonly actionMethod: () => void;

    constructor(
        pageContext: IVssPageContext,
        onUnlistPackage: (
            pageContext: IVssPageContext,
            packageSummary: Package,
            packageVersion: PackageVersion,
            feed: Feed,
            viaPackageList: boolean
        ) => Promise<void>,
        packageSummary: Package,
        packageVersion: PackageVersion,
        feed: Feed,
        viaPackageList: boolean,
        disabled: boolean
    ) {
        this.id = "unlist";
        this.protocolCommand = ProtocolCommands.Unlist;
        this.displayText = PackageResources.PackageCommands_UnlistPackage;
        this.icon = "bowtie-icon bowtie-math-minus";
        this.disabled = disabled;
        this.actionMethod = () => onUnlistPackage(pageContext, packageSummary, packageVersion, feed, viaPackageList);
    }

    public static onUnlistPackage(
        pageContext: IVssPageContext,
        packageSummary: Package,
        packageVersion: PackageVersion,
        feed: Feed,
        viaPackageList: boolean
    ): Promise<void> {
        const nuGetDataService = pageContext.getService<IFeedNuGetDataService>("IFeedNuGetDataService");

        CustomerIntelligenceHelper.publishEvent(pageContext, NuGetCiConstants.UnlistPackage, {
            feedId: feed.id,
            feedName: feed.name,
            viewId: feed.view ? feed.view.id : undefined,
            viewName: feed.view ? feed.view.name : undefined,
            packageName: packageSummary.name,
            packageVersion: packageVersion.version
        });

        const packageVersionDetails = {
            listed: false
        } as PackageVersionDetails;

        const projectId = feed.project ? feed.project.id : null;
        return nuGetDataService
            .updatePackageVersion(
                packageVersionDetails,
                projectId,
                feed.id,
                packageSummary.normalizedName,
                packageVersion.normalizedVersion
            )
            .then(
                success => {
                    if (!viaPackageList) {
                        packageVersion.isListed = false;
                        const packagePayload = {
                            packageSummary,
                            packageVersion
                        } as IPackagePayload;

                        Actions.PackageModified.invoke(packagePayload);
                        announce(
                            Utils_String.format(
                                PackageResources.PackageUnlisted_Announcement_Version_One,
                                packageVersion.version
                            )
                        );
                    } else {
                        Actions.PackageListedStatusChanged.invoke({
                            packageId: packageSummary.id,
                            isListed: false
                        });
                        announce(
                            Utils_String.format(PackageResources.PackageUnlisted_Announcement_One, packageSummary.name)
                        );
                    }
                },
                err => {
                    if (err.status === 401) {
                        Actions.ErrorEncountered.invoke({
                            message: PackageResources.Error_ErrorUserUnauthorized,
                            details: err
                        } as IError);
                    } else {
                        Actions.ErrorEncountered.invoke({
                            message:
                                err.serverError && err.serverError.reason
                                    ? err.serverError.reason
                                    : PackageResources.Error_ErrorUnlistingPackage,
                            isCritical: true,
                            details: err
                        } as IError);
                    }
                }
            );
    }
}
